import { computed, shallowRef } from 'vue';
import { Preferences } from '@capacitor/preferences';
import { updatePrivacyConsents } from '@/api/user/privacy-consents.api';
import {
  CONSENT_PURPOSES,
  type ConsentPurpose,
  type ConsentSource,
  type PrivacyConsentChanges,
  type PrivacyConsents,
  type PurposeConsent,
} from '@/api/user/types/privacy-consents';

/**
 * The signed-in account's optional privacy consents, as stored by the backend.
 *
 * The only sources are GET /api/user and the PUT /api/user/privacy-consents response. Nothing
 * is read from device storage or inferred from other flags, and other user responses (login,
 * profile or avatar updates) carry no consents, so they never clear what is known here.
 * Unknown means not granted.
 *
 * The one exception is a revocation the backend has not stored yet (the request failed, or the
 * app closed first). It is kept on the device per account and overrides a stored grant, across
 * restarts and sign-outs, until the backend stores the revocation or the user grants the
 * purpose again. It can only deny, never grant.
 */

/** Matches the Privacy Policy "last updated" date (privacy-policy.content.ts). */
export const PRIVACY_POLICY_VERSION = '2026-11-03';
/** Raise to ask every user again (e.g. a new purpose); answered versions are stored server-side. */
export const CONSENT_PROMPT_VERSION = 1;

export type ConsentChoice = 'granted' | 'denied' | 'unset';

const state = shallowRef<{ accountId: string; consents: PrivacyConsents } | null>(null);
// Pending revocations per account; read from the device once per app session.
const pendingRevocations = shallowRef<Record<string, ConsentPurpose[]>>({});
const pendingLoads = new Map<string, Promise<void>>();
const PENDING_REVOCATIONS_KEY_PREFIX = 'privacyConsents.pendingRevocations.';

const time = (value: string | null) => (value ? Date.parse(value) || 0 : 0);

// A response that started before a later change can arrive after it: keep the newer choice.
const newer = (current: PurposeConsent, incoming: PurposeConsent) =>
  time(incoming.updatedAt) >= time(current.updatedAt) ? incoming : current;

const merge = (current: PrivacyConsents, incoming: PrivacyConsents): PrivacyConsents => {
  const promptFromIncoming = (incoming.promptVersion ?? 0) >= (current.promptVersion ?? 0);
  return {
    metaAdMeasurement: newer(current.metaAdMeasurement, incoming.metaAdMeasurement),
    sessionReplay: newer(current.sessionReplay, incoming.sessionReplay),
    promptVersion: promptFromIncoming ? incoming.promptVersion : current.promptVersion,
    promptAnsweredAt: promptFromIncoming ? incoming.promptAnsweredAt : current.promptAnsweredAt,
  };
};

const pendingFor = (accountId: string | null | undefined) => (accountId && pendingRevocations.value[accountId]) || [];

const setPending = async (accountId: string, purposes: ConsentPurpose[]) => {
  pendingRevocations.value = { ...pendingRevocations.value, [accountId]: purposes };
  const key = `${PENDING_REVOCATIONS_KEY_PREFIX}${accountId}`;
  try {
    if (purposes.length) await Preferences.set({ key, value: JSON.stringify(purposes) });
    else await Preferences.remove({ key });
  } catch {
    // Still in force for this app session.
  }
};

/** Reads the account's pending revocations from the device. Call before its consents are used. */
export const loadPendingRevocations = (accountId: string): Promise<void> => {
  let load = pendingLoads.get(accountId);
  if (!load) {
    load = (async () => {
      let stored: ConsentPurpose[] = [];
      try {
        const { value } = await Preferences.get({ key: `${PENDING_REVOCATIONS_KEY_PREFIX}${accountId}` });
        if (value) {
          const parsed: unknown = JSON.parse(value);
          stored = CONSENT_PURPOSES.filter(purpose => Array.isArray(parsed) && parsed.includes(purpose));
        }
      } catch {
        // Unreadable or corrupt: something may have been revoked, so keep every purpose off.
        stored = [...CONSENT_PURPOSES];
      }
      const current = pendingFor(accountId);
      const merged = [...current, ...stored.filter(purpose => !current.includes(purpose))];
      if (merged.length !== current.length)
        pendingRevocations.value = { ...pendingRevocations.value, [accountId]: merged };
    })();
    pendingLoads.set(accountId, load);
  }
  return load;
};

/** A revocation of this purpose is waiting to be stored by the backend. */
export const isRevocationPending = (accountId: string | null | undefined, purpose: ConsentPurpose) =>
  pendingFor(accountId).includes(purpose);

export const hasPendingRevocations = (accountId: string | null | undefined) => pendingFor(accountId).length > 0;

const markRevocationsPending = async (accountId: string, purposes: ConsentPurpose[]) => {
  await loadPendingRevocations(accountId);
  const current = pendingFor(accountId);
  const added = purposes.filter(purpose => !current.includes(purpose));
  if (added.length) await setPending(accountId, [...current, ...added]);
};

const clearPendingRevocations = async (accountId: string, purposes: ConsentPurpose[]) => {
  const current = pendingFor(accountId);
  const next = current.filter(purpose => !purposes.includes(purpose));
  if (next.length !== current.length) await setPending(accountId, next);
};

/** GET /api/user: the consents of the signed-in account (switches account when it changed). */
export const applyServerPrivacyConsents = (accountId: string, incoming?: PrivacyConsents | null) => {
  if (!incoming) return;
  const current = state.value?.accountId === accountId ? state.value.consents : null;
  state.value = { accountId, consents: current ? merge(current, incoming) : incoming };
};

/** Signed out: forget the previous account's consents. */
export const clearPrivacyConsents = () => {
  state.value = null;
};

/**
 * Consents of this account, or null while unknown (not loaded, or another account). A pending
 * revocation shows as not granted.
 */
export const getPrivacyConsents = (accountId: string | null | undefined): PrivacyConsents | null => {
  if (!accountId || state.value?.accountId !== accountId) return null;
  const consents = state.value.consents;
  const revoked = pendingFor(accountId).filter(purpose => consents[purpose].granted);
  if (!revoked.length) return consents;
  const effective = { ...consents };
  for (const purpose of revoked) effective[purpose] = { ...consents[purpose], granted: false };
  return effective;
};

export const consentChoice = (consent?: PurposeConsent | null): ConsentChoice => {
  if (!consent) return 'unset';
  if (consent.granted) return 'granted';
  return consent.updatedAt ? 'denied' : 'unset';
};

/** The account's stored choice for one purpose ('unset' when unknown). */
export const getConsentChoice = (accountId: string | null | undefined, purpose: ConsentPurpose): ConsentChoice =>
  isRevocationPending(accountId, purpose) ? 'denied' : consentChoice(getPrivacyConsents(accountId)?.[purpose]);

/** The account has not answered the current consent prompt yet. */
export const needsConsentPrompt = (consents: PrivacyConsents | null) =>
  !!consents && (consents.promptVersion ?? 0) < CONSENT_PROMPT_VERSION;

export const usePrivacyConsents = (accountId: () => string | null | undefined) =>
  computed(() => getPrivacyConsents(accountId()));

// One request at a time, so a retried revocation can never land after a later choice.
let queue: Promise<unknown> = Promise.resolve();
const serialized = <T>(task: () => Promise<T>): Promise<T> => {
  const run = queue.then(task, task);
  queue = run.catch(() => undefined);
  return run;
};

const save = async (
  accountId: string,
  changes: PrivacyConsentChanges,
  source: ConsentSource,
  promptVersion?: number
): Promise<PrivacyConsents | null> => {
  const purposes = CONSENT_PURPOSES.filter(purpose => changes[purpose] !== undefined);
  // Revoking a granted purpose: keep it revoked on the device until the backend stores it.
  const revoking = purposes.filter(purpose => !changes[purpose] && getConsentChoice(accountId, purpose) === 'granted');
  if (revoking.length) await markRevocationsPending(accountId, revoking);
  const consents = await updatePrivacyConsents({
    ...changes,
    policyVersion: PRIVACY_POLICY_VERSION,
    source,
    ...(promptVersion ? { promptVersion } : {}),
  });
  // Signed out or switched account while saving: the result belongs to nobody on screen.
  const loaded = state.value?.accountId === accountId;
  // Apply the stored result first, then drop the pending revocation: the other way round, the
  // old grant would be in force again for a moment (and Replay / Meta could resume).
  if (loaded) applyServerPrivacyConsents(accountId, consents);
  // Stored: the backend now has the latest choice for these purposes.
  await clearPendingRevocations(accountId, purposes);
  if (!loaded || state.value?.accountId !== accountId) return null;
  return getPrivacyConsents(accountId);
};

/**
 * Stores choices for the signed-in account. Throws when the request fails; nothing is applied
 * locally then, except that a revocation stays pending (see above). Each purpose is
 * independent: only the purposes passed are changed.
 */
export const savePrivacyConsents = (
  accountId: string,
  changes: PrivacyConsentChanges,
  source: ConsentSource,
  promptVersion?: number
): Promise<PrivacyConsents | null> => serialized(() => save(accountId, changes, source, promptVersion));

/**
 * Sends the signed-in account's pending revocations again (after GET /api/user, or from
 * Settings). Returns whether none is left pending; never throws.
 */
export const retryPendingRevocations = (accountId: string): Promise<boolean> =>
  serialized(async () => {
    await loadPendingRevocations(accountId);
    const pending = pendingFor(accountId);
    // Only for the account whose consents are loaded, i.e. the one signed in.
    if (!pending.length || state.value?.accountId !== accountId) return !pending.length;
    const stored = state.value.consents;
    // Already stored as not granted (e.g. the request reached the backend but the response was lost).
    const done = pending.filter(purpose => !stored[purpose].granted);
    if (done.length) await clearPendingRevocations(accountId, done);
    const remaining = pendingFor(accountId);
    if (!remaining.length) return true;
    try {
      await save(accountId, Object.fromEntries(remaining.map(purpose => [purpose, false])), 'settings');
      return !hasPendingRevocations(accountId);
    } catch {
      return false;
    }
  });

/** Test-only reset of the app session's state (pending revocations stay on the device). */
export const __resetPrivacyConsentsForTests = () => {
  state.value = null;
  pendingRevocations.value = {};
  pendingLoads.clear();
  queue = Promise.resolve();
};
